import { Log } from '../../AuxillaryService/Log';
import { createReachVideoHandler } from '../../MediaController';
import { GenericVideoHandler } from '../../MediaController/Video/Generic';
import { Permission } from '../../Platform';
import { ReachAPI } from '../../ReachApi';
import { Signage } from '../../Signage';
import { assert } from '../../Utility/assert';
import { Widget } from '../Widget';
import { BaseWidgetConfiguration, FadeInCommand, FadeOutCommand } from '../types';
import AdQueue from './AdQueue';
import {
    BaseThirdPartyAdWidgetWidgetTimelineSlot,
    BaseThirdPartyAd,
    BaseThirdPartyAdWidgetConfigurationDetails,
    ZodBaseThirdPartyAdWidgetConfigurationDetails,
    ValidAssetTypes,
    mappedBaseThirdPartyFallbackAdFileTypes,
} from './types';
import { randomBytes } from 'crypto';
import './basethirdpartyad.scss';

export abstract class BaseThirdPartyAdWidget extends Widget {
    declare config: BaseThirdPartyAdWidgetConfigurationDetails;

    protected fallbackAd: BaseThirdPartyAd;
    /**
     * player for videos
     */
    protected videoPlayer: GenericVideoHandler | null;

    /**
     * Queue of ads retrieved from ad vendor
     */
    protected abstract adQueue: AdQueue<BaseThirdPartyAd>;

    /**
     *  Width for requested ads
     */
    protected adWidth: number;

    /**
     *  Height for requested ads
     */
    protected adHeight: number;

    /**
     * Ad duration in seconds
     */
    protected durationInSeconds: number;

    /**
     * Number of ads to display back to back
     */
    protected numberOfAds: number;

    /**
     * Mutes any video ads being played on the signage
     */
    protected muteAds: boolean;

    /**
     *
     * Uuid of the fallback asset to display when there are no ads
     */
    protected fallbackAssetUuid: string;

    /**
     * epoch time of when the 'current' loop started playing.
     */
    protected startTime: number;

    /**
     * whether signage is configured to play reach videos
     */
    protected canPlayReachVideos: boolean;

    /**
     * tracks the number of times querySlotsLinear() was called,
     * the purpose is so getAds() will fetch 2x the amount of ads
     * for the first two consecutive calls of querySlotsLinear()
     */
    protected querySlotsCallNo: number;

    constructor(widgetConfiguration: BaseWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);
        this.adWidth = this.config.ad_width;
        this.adHeight = this.config.ad_height;
        this.durationInSeconds = this.config.duration_in_seconds;
        this.numberOfAds = this.config.number_of_ads;
        this.muteAds = this.config.mute_ads;
        this.fallbackAssetUuid = this.config.fallback_asset_uuid;
        this.startTime = 0;
        this.canPlayReachVideos = Signage.platform.hasPermission(Permission.PLAY_REACH_VIDEO);
        this.querySlotsCallNo = 1;
        this.videoPlayer = null;

        this.fallbackAd = this.createFallBackAd(
            this.config.fallback_asset_uuid,
            mappedBaseThirdPartyFallbackAdFileTypes[this.config.fallback_asset_type]
        );
    }

    async initialize(): Promise<void> {
        await this.getAds();
        await super.initialize();
    }

    protected validateConfigurationDetails(
        baseThirdPartyAdWidgetConfigurationDetails: BaseThirdPartyAdWidgetConfigurationDetails
    ): BaseThirdPartyAdWidgetConfigurationDetails {
        return ZodBaseThirdPartyAdWidgetConfigurationDetails.parse(
            baseThirdPartyAdWidgetConfigurationDetails
        );
    }

    /**
     * Attempts to fetch ads if adqueue contains
     * less ads than requested on the widget
     * configuration.
     */
    async getReadyForDisplay(): Promise<void> {
        if (this.adQueue.size() <= this.numberOfAds) {
            await this.getAds();
        }
    }

    /**
     * fetches ads from ad vendors API
     */
    abstract getAds(): void;

    async afterFadeIn(
        currentSlot: BaseThirdPartyAdWidgetWidgetTimelineSlot
    ): Promise<void | Partial<FadeInCommand>> {
        Log.Info(
            ` ${this.name} : showing ad ${currentSlot.params.ad.adId} & group ID ${currentSlot.groupIdentifier}`,
            null,
            __fileline
        );
        if (currentSlot.params.ad.filetype === 'video') {
            try {
                await this.videoPlayer?.play();
            } catch (error) {
                Log.Error(`${this.name}: Failed to play video` + String(error), null, __fileline);
            }
        }
    }

    async stop(): Promise<void> {
        if (this.videoPlayer) {
            await this.videoPlayer.stop();
            this.videoPlayer = null;
        }
        await super.stop();
    }

    /**
     * attempts to make an AdObject
     * from fallback config data
     */
    protected createFallBackAd(
        fallbackAssetUuid: string,
        fallbackAssetType: ValidAssetTypes
    ): BaseThirdPartyAd {
        /* empty str for video as fallback asset url will be computed
         * when the Reach video player is created.
         */
        const fallBackAssetUrl: string =
            fallbackAssetType === 'image'
                ? ReachAPI.server + '/cdn/asset/' + fallbackAssetUuid
                : '';
        return {
            assetUrl: fallBackAssetUrl,
            adId: String(this.id),
            filetype: fallbackAssetType,
            duration: this.durationInSeconds,
            isFallback: true,
            playcount: 0,
            lastAdInLoop: true,
        };
    }

    /**
     * Creates vendor specific video handler
     */
    abstract getThirdPartyAdVendorVideoHandler(assetUrl: string): GenericVideoHandler;

    /**
     * generates 10 char long random id
     */
    protected generateId(): string {
        const bytes = randomBytes(5);
        return bytes.toString('hex').substring(0, 10);
    }

    /**
     *  Generates random number in the
     *  range [1,500000]
     */
    protected generateGroupId(): number {
        const bytes = randomBytes(3);
        const number = (parseInt(bytes.toString('hex'), 16) % 500_000) + 1;
        return number;
    }

    async beforeFadeOut(
        currentSlot: BaseThirdPartyAdWidgetWidgetTimelineSlot
    ): Promise<void | Partial<FadeOutCommand>> {
        if (currentSlot.params.ad.filetype === 'video') {
            await this.videoPlayer?.stop();
        }
        return { cancelFade: false };
    }

    /**
     * Triggered before fade in occurs
     */
    async beforeFadeIn(
        currentSlot: BaseThirdPartyAdWidgetWidgetTimelineSlot
    ): Promise<void | Partial<FadeInCommand>> {
        this.startTime = Date.now();
        const anAd = currentSlot.params.ad;
        if (anAd.filetype === 'image') {
            const imageElement: HTMLImageElement = document.createElement('img');
            imageElement.id = `${this.name}_ad_asset_${String(anAd.adId)}`;
            imageElement.src = anAd.assetUrl;
            imageElement.className = 'base-third-party-ad-image';
            imageElement.alt = `${this.name} Ad : ${anAd.adId}`;
            imageElement.title = anAd.adId;
            this.wrapperElement.appendChild(imageElement);
        } else {
            if (anAd.isFallback && this.canPlayReachVideos === false) {
                Log.Error('Signage not configured to play reach videos', null, __fileline);
                return;
            }

            const videoContainer: HTMLDivElement = document.createElement('div');
            //if video ad
            // if third party ad vendor ad
            this.videoPlayer = !anAd.isFallback
                ? this.getThirdPartyAdVendorVideoHandler(anAd.assetUrl)
                : createReachVideoHandler({
                      videoUuid: this.fallbackAssetUuid,
                      muted: this.muteAds,
                  });

            await this.videoPlayer.setElement(videoContainer);
            this.wrapperElement.appendChild(videoContainer);
        }
    }

    async afterFadeOut(currentSlot: BaseThirdPartyAdWidgetWidgetTimelineSlot): Promise<void> {
        this.removeAdFromDom(currentSlot.params.ad);
        this.startTime = 0;
        /*
         * re-implementing how shuffling
         * works could break this widget as
         * the prop 'lastAdInLoop' relies on the
         * ad slots playing in the order
         * in which they were pushed in querySlotsLinear()
         */
        if (
            this.querySlotsCallNo >= 3 &&
            currentSlot.params.ad.lastAdInLoop === true &&
            this.adQueue.size() <= this.numberOfAds
        ) {
            await this.getAds();
        }
    }

    /**
     * removes ads elements and content from dom
     */
    private removeAdFromDom(anAd: BaseThirdPartyAd): void {
        if (anAd.filetype === 'image') {
            const expectedElementId = `${this.name}_ad_asset_${String(anAd.adId)}`;
            const elementToRemove = this.wrapperElement.firstElementChild;
            if (!elementToRemove || elementToRemove.id !== expectedElementId) {
                Log.Error(
                    `${this.name}: Failed to remove ad element for ad ${anAd.adId}`,
                    null,
                    __fileline
                );
                return;
            }
            elementToRemove.remove();
        } else {
            //remove video player container from dom
            try {
                assert(this.videoPlayer !== null, 'Player is not set.');
                const videoContainer = this.videoPlayer.getElement();
                this.wrapperElement.removeChild(videoContainer);
            } catch (err) {
                Log.Error(
                    `${this.name}: failed to remove video container -- ${String(err)}`,
                    null,
                    __fileline
                );
            }
            this.videoPlayer = null;
        }
    }

    /**
     * gets all available content to send to the timeline
     * probably should never use timebased with
     * this widget.
     */
    querySlotsLinear(timeBased: boolean): BaseThirdPartyAdWidgetWidgetTimelineSlot[] {
        const slots: BaseThirdPartyAdWidgetWidgetTimelineSlot[] = [];
        const groupId: number = this.generateGroupId();
        if (timeBased) {
            let usedTime = 0;
            do {
                const currentTimeInSeconds = Math.floor(Date.now() / 1000);
                // if 0 ads in queue push fallback
                if (this.adQueue.size() === 0) {
                    const tempFallbackAd: BaseThirdPartyAd = Object.assign({}, this.fallbackAd);
                    tempFallbackAd.adId += this.generateId();
                    tempFallbackAd.playcount++;
                    slots.push(
                        this.generateSlot(this.fallbackAd.duration, groupId, {
                            ad: tempFallbackAd,
                        })
                    );
                    break;
                } else {
                    const tempAd = this.adQueue.dequeue();
                    if (!tempAd) {
                        break;
                    }
                    if (tempAd.expires === undefined || currentTimeInSeconds < tempAd.expires) {
                        tempAd.adId += this.generateId();
                        const slot = this.generateSlot(tempAd.duration, groupId, {
                            ad: tempAd,
                        });
                        tempAd.playcount++;
                        slots.push(slot);
                        usedTime += tempAd.duration;
                    }
                }
            } while (usedTime < this.minimumLoopTime);
        } else {
            //not time based
            //handle 0 ads in ad queue
            if (this.adQueue.size() === 0) {
                const tempFallbackAd: BaseThirdPartyAd = Object.assign({}, this.fallbackAd);
                tempFallbackAd.adId += this.generateId();
                tempFallbackAd.playcount++;
                const slot = this.generateSlot(this.fallbackAd.duration, groupId, {
                    ad: tempFallbackAd,
                });
                slots.push(slot);
            } else {
                while (this.adQueue.size() > 0 && slots.length < this.numberOfAds) {
                    const currentTimeInSeconds = Math.floor(Date.now() / 1000);
                    const tempAd = this.adQueue.dequeue();
                    if (tempAd) {
                        if (tempAd.expires === undefined || currentTimeInSeconds < tempAd.expires) {
                            tempAd.adId += this.generateId();
                            const slot = this.generateSlot(tempAd.duration, groupId, {
                                ad: tempAd,
                            });
                            tempAd.playcount++;
                            slots.push(slot);
                        } else {
                            Log.Warning(
                                `${this.name}: Discarding expired ad ID: ${tempAd?.adId}`,
                                null,
                                __fileline
                            );
                        }
                    }
                }
            }
        }
        if (this.querySlotsCallNo <= 3) {
            this.querySlotsCallNo++;
        }
        if (slots.length > 0) {
            slots[slots.length - 1].params.ad.lastAdInLoop = true;
        }
        return slots;
    }

    querySlots(): BaseThirdPartyAdWidgetWidgetTimelineSlot[] {
        Log.Warning(
            'Loop-based timelines are not supported by this widget. Returning no slots.',
            null,
            __fileline
        );
        return [];
    }
}
